/**
 * หน้าเข้าสู่ระบบ — ทางเข้าเดียวของทั้งสอง shell
 *
 * ตัวเดียวใช้ทั้ง Desktop และเครื่องสแกนโดยเจตนา เพราะข้อบังคับที่สำคัญที่สุดของ
 * หน้าจอนี้คือ **ความล้มเหลวทุกแบบต้องอ่านเหมือนกัน** ถ้าแยกเป็นสองหน้าจอ วันหนึ่ง
 * หนึ่งในนั้นจะเผลอบอกมากกว่าอีกอัน ต่างกันแค่ `device_kind` ที่ส่งไปกับคำขอและ
 * ขนาดปุ่ม/ช่องกรอกสำหรับผู้สวมถุงมือ
 *
 * **เส้นทางกู้คืนของ D-050 จบที่หน้าจอนี้ ไม่ใช่ที่ผู้ดูแลระบบ**: `403` ที่บอกว่าต้อง
 * เปลี่ยนรหัสผ่านก่อน แนบ `password_change_token` มาด้วย หน้าจอจึงสลับไปเรนเดอร์
 * `ChangePasswordForm` ให้ตั้งรหัสใหม่ได้ที่นี่เลย token นั้น**อยู่ใน state ของ
 * component เท่านั้น** ไม่เคยถูกเขียนลง storage (D-049 อนุญาตให้เก็บเฉพาะ session
 * token) และถูกทิ้งทันทีที่เปลี่ยนรหัสสำเร็จ เพราะเซิร์ฟเวอร์ฆ่ามันไปแล้วในคำขอนั้น
 * ส่วนคนที่กด "ยกเลิก" กลางทาง token ใบนั้นยัง**เป็น**ของจริงอยู่ จึงต้องถูกปิดด้วย
 * `POST /v1/auth/logout` (D-057) ไม่ใช่แค่ลืมทิ้งไว้ให้ค้างจนหมดอายุ
 *
 * สิ่งที่หน้าจอนี้ตั้งใจ **ไม่** ทำ:
 * - ไม่พาผู้ใช้เข้าระบบเองหลังเปลี่ยนรหัสผ่านสำเร็จ D-050 ฆ่าทุก session รวมทั้ง
 *   token ที่เพิ่งใช้ การพยายามใช้มันต่อจะได้ `401` ที่อธิบายไม่ได้ ผู้ใช้จึงกลับมา
 *   ที่ฟอร์มเข้าสู่ระบบพร้อมข้อความว่าให้เข้าใหม่ด้วยรหัสผ่านใหม่
 * - ไม่มีตัวเลือก role/โรงงาน/อุปกรณ์ `device_kind` มาจาก shell ไม่ใช่จากผู้ใช้ และ
 *   contract ระบุว่ามันเลือกได้แค่อายุ session ไม่ใช่สิทธิ์
 * - ไม่เก็บรหัสผ่านไว้ใน state ของ React เลย ช่องรหัสผ่านเป็น uncontrolled input
 *   และถูกล้างทันทีที่ submit จบ รหัสผ่านจึงมีอายุเท่ากับการเรียก `login()` ครั้งนั้น
 */

import { useRef, useState, type FormEvent, type ReactNode } from 'react';

import { ChangePasswordForm } from './ChangePasswordForm';
import type { DeviceKind, LoginResult } from './client';
import { login } from './client';
import './login.css';
import { logout } from './logoutClient';
import { startSession, useSession } from './session';
import { primaryTouchTargetStyle, touchTargetStyle } from '../../scanner/touch';

/**
 * ข้อความเดียวของความล้มเหลวด้าน credential ทั้งหมด
 *
 * `docs/API_CONTRACT.md`: username ที่ไม่มีจริง, รหัสผิด, credential ที่ถูก revoke,
 * บัญชีที่ถูกล็อกชั่วคราว และพนักงาน INACTIVE ตอบ `401` ที่เหมือนกันทุกประการ —
 * ตั้งใจให้แยกไม่ออก **ห้ามเพิ่มสถานะ "ไม่พบผู้ใช้" หรือบอกว่าบัญชีถูกล็อก** เพราะ
 * นั่นคือการทำให้หน้าจอกลายเป็น oracle ที่ API ปฏิเสธที่จะเป็น
 */
export const LOGIN_REJECTED_MESSAGE = 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง';

export const MISSING_INPUT_MESSAGE = 'กรอกชื่อผู้ใช้และรหัสผ่านก่อนกดเข้าสู่ระบบ';

/**
 * ข้อความหลังเปลี่ยนรหัสผ่านสำเร็จบนเส้นทางกู้คืน
 *
 * บอกตรง ๆ ว่าต้องเข้าสู่ระบบใหม่ เพราะ D-050 ฆ่าทุก session รวมทั้ง token ที่เพิ่งใช้
 * — ผู้ใช้ที่ไม่รู้ข้อนี้จะคิดว่าการเปลี่ยนรหัสล้มเหลวเมื่อเห็นหน้า Login อีกครั้ง
 */
export const PASSWORD_CHANGED_MESSAGE =
  'เปลี่ยนรหัสผ่านเรียบร้อยแล้ว — เข้าสู่ระบบอีกครั้งด้วยรหัสผ่านใหม่';

/** ข้อความของความล้มเหลวที่ **ไม่ใช่** เรื่อง credential จึงบอกตรง ๆ ได้ */
function describeFailure(result: LoginResult): string {
  switch (result.status) {
    case 'rejected':
      return LOGIN_REJECTED_MESSAGE;
    case 'forbidden':
      return 'เซิร์ฟเวอร์ปฏิเสธคำขอเข้าสู่ระบบนี้';
    case 'bad-request':
      return 'คำขอเข้าสู่ระบบไม่ถูกต้องตามรูปแบบที่เซิร์ฟเวอร์รับ';
    case 'server-error':
      return `เซิร์ฟเวอร์ตอบ ${result.httpStatus} — ยังเข้าสู่ระบบไม่ได้`;
    case 'network-error':
      return 'ติดต่อเซิร์ฟเวอร์ไม่ได้ — ยังเข้าสู่ระบบไม่ได้';
    case 'invalid-response':
      return 'คำตอบจากเซิร์ฟเวอร์ผิดรูปแบบ — ยังเข้าสู่ระบบไม่ได้';
    case 'password-change-required':
    case 'ready':
      // สองสถานะนี้ไม่ได้เรนเดอร์ผ่านกล่องข้อความผิดพลาด — แจกแจงไว้ให้ TypeScript
      // พิสูจน์ว่าครบทุกสถานะ แทนที่จะมี `default` ที่กลืนสถานะใหม่เงียบ ๆ
      return '';
  }
}

/** คำอธิบายว่าอุปกรณ์นี้ได้อายุ session แบบไหน — ไม่ใช่ตัวเลือกและไม่ใช่สิทธิ์ */
function describeDeviceKind(deviceKind: DeviceKind): string {
  return deviceKind === 'HANDHELD'
    ? 'เครื่องสแกนหน้างาน — เซสชันยาวพอสำหรับหนึ่งกะ (D-046) ค่านี้กำหนดอายุเซสชันเท่านั้น ไม่ใช่สิทธิ์'
    : 'หน้าจอ Desktop — ค่านี้กำหนดอายุเซสชันเท่านั้น ไม่ใช่สิทธิ์';
}

/**
 * ความล้มเหลวที่หน้าจอนี้แสดง
 *
 * `missing-input` ไม่ใช่คำตอบของเซิร์ฟเวอร์ — เป็นการที่ผู้ใช้ยังกรอกไม่ครบ จึงแยก
 * ออกจาก `LoginResult` เพื่อไม่ให้ปนกับ `bad-request` ที่แปลว่าเซิร์ฟเวอร์ปฏิเสธ
 * รูปแบบคำขอจริง ๆ (สองอย่างนี้ต้องบอกผู้ใช้คนละเรื่อง)
 */
type LoginFailure =
  | { readonly kind: 'missing-input' }
  | { readonly kind: 'result'; readonly result: LoginResult };

export interface LoginPageProps {
  /** มาจาก shell ที่กำลังใช้อยู่ ไม่ใช่จากผู้ใช้ */
  readonly deviceKind: DeviceKind;
  /** inject ได้เพื่อการทดสอบ — production ใช้ค่าจาก `VITE_API_BASE_URL` */
  readonly apiBaseUrl?: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

/**
 * กรอบของหน้าจอนี้ — ใช้ร่วมกันระหว่างฟอร์มเข้าสู่ระบบและฟอร์มตั้งรหัสผ่านใหม่
 *
 * ทั้งสองสถานะเป็น "หน้าจอก่อนเข้าระบบ" อันเดียวกัน ต่างกันแค่สิ่งที่อยู่ในการ์ด
 * `data-screen` บอกว่าตอนนี้คือสถานะไหน เพื่อให้เทสต์ (และคนอ่าน DOM) ไม่ต้องเดา
 */
function LoginCard({
  deviceKind,
  screen,
  children,
}: {
  readonly deviceKind: DeviceKind;
  readonly screen: 'login' | 'password-change';
  readonly children: ReactNode;
}) {
  return (
    <div
      className="cap-login"
      data-testid="login-page"
      data-device-kind={deviceKind}
      data-screen={screen}
    >
      <main className="cap-login__card">
        <div className="cap-login__brand">
          <img
            className="cap-login__logo"
            src="/icons/cap-logo-mark.png"
            width={180}
            height={70}
            alt="CAP"
          />
          <span className="cap-login__org">CAP ERP</span>
        </div>
        {children}
      </main>
    </div>
  );
}

export function LoginPage({ deviceKind, apiBaseUrl, fetchImpl }: LoginPageProps) {
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<LoginFailure | null>(null);
  /**
   * `password_change_token` ของ D-050 — **state ของ component เท่านั้น**
   *
   * ไม่ลง `localStorage`, ไม่ลง session store และไม่ลง URL: token ที่รอดการรีเฟรช
   * คือสถานะกึ่งเข้าสู่ระบบที่เซิร์ฟเวอร์ตั้งใจไม่ออกให้ การรีเฟรชจึงพากลับมาที่ฟอร์ม
   * เข้าสู่ระบบซึ่งจะออก token ใบใหม่ให้เองเมื่อรหัสผ่านถูกต้อง
   */
  const [recoveryToken, setRecoveryToken] = useState<string | null>(null);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const { endedReason } = useSession();

  const handheld = deviceKind === 'HANDHELD';
  const fieldStyle = handheld ? touchTargetStyle : undefined;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) {
      return;
    }

    const username = usernameRef.current?.value.trim() ?? '';
    // รหัสผ่านไม่ถูก trim — ช่องว่างหัวท้ายเป็นส่วนหนึ่งของรหัสผ่านที่ผู้ใช้ตั้งไว้จริง
    const password = passwordRef.current?.value ?? '';
    if (username === '' || password === '') {
      setFailure({ kind: 'missing-input' });
      return;
    }

    setSubmitting(true);
    setFailure(null);
    setPasswordChanged(false);

    void login(
      { username, password, deviceKind },
      {
        ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }),
        ...(fetchImpl === undefined ? {} : { fetchImpl }),
      },
    ).then((result) => {
      // ล้างช่องรหัสผ่านทันทีไม่ว่าผลจะเป็นอย่างไร — ไม่ปล่อยให้ค้างอยู่ใน DOM
      if (passwordRef.current !== null) {
        passwordRef.current.value = '';
      }

      if (result.status === 'ready') {
        // บรรทัดสุดท้ายของ component นี้ในรอบชีวิตของมัน: `App` จะสลับไปเรนเดอร์
        // shell ทันทีที่ store แจ้ง จึงไม่ตั้ง state ใด ๆ ต่อจากนี้
        startSession(result.token);
        return;
      }

      if (result.status === 'password-change-required' && result.passwordChangeToken !== null) {
        // ทางออกของ D-050: ถือ token ไว้ใน state แล้วสลับไปฟอร์มตั้งรหัสผ่านใหม่
        // — ไม่ใช่เก็บลง storage และไม่ใช่บอกให้ไปตามผู้ดูแลระบบมารีเซ็ตซ้ำ ซึ่งจะ
        // พาผู้ใช้กลับมาที่สถานะเดิมนี้อีกครั้ง
        setRecoveryToken(result.passwordChangeToken);
        setSubmitting(false);
        return;
      }

      setFailure({ kind: 'result', result });
      setSubmitting(false);
    });
  };

  if (recoveryToken !== null) {
    return (
      <LoginCard deviceKind={deviceKind} screen="password-change">
        <ChangePasswordForm
          purpose="RECOVERY"
          token={recoveryToken}
          deviceKind={deviceKind}
          onChanged={() => {
            // ทิ้ง token ทันที: เซิร์ฟเวอร์ revoke มันไปแล้วพร้อมกับ session อื่น
            // ทั้งหมดในคำขอที่เพิ่งสำเร็จ (D-050) การเก็บไว้ใช้ต่อจะได้ `401`
            // ที่อธิบายไม่ได้ ส่วนผู้ใช้กลับมาที่ฟอร์มเข้าสู่ระบบพร้อมคำอธิบาย
            setRecoveryToken(null);
            setPasswordChanged(true);
          }}
          onCancel={() => {
            /**
             * เปลี่ยนใจ = **ปิด session ที่ token ใบนั้นถืออยู่** ไม่ใช่แค่ทิ้งมัน
             *
             * D-057 ระบุว่า endpoint นี้รับ `password_change_token` ของ D-050 ด้วย
             * ด้วยเหตุผลนี้ตรง ๆ: token ที่ถูกทิ้งไว้เฉย ๆ ยังใช้ได้จนหมดอายุ
             * ยิงคำขอก่อนแล้วทิ้ง token ทันที — เหมือน `signOut()` คำตอบของ
             * เซิร์ฟเวอร์เปลี่ยนผลบนเครื่องนี้ไม่ได้ และ `401` ก็คือจบแล้วเช่นกัน
             */
            void logout({
              token: recoveryToken,
              ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }),
              ...(fetchImpl === undefined ? {} : { fetchImpl }),
            });
            setRecoveryToken(null);
          }}
          {...(apiBaseUrl === undefined ? {} : { apiBaseUrl })}
          {...(fetchImpl === undefined ? {} : { fetchImpl })}
        />
      </LoginCard>
    );
  }

  /**
   * `403 PASSWORD_CHANGE_REQUIRED` ที่ **ไม่มี** token มาด้วย
   *
   * เกิดกับเซิร์ฟเวอร์รุ่นก่อน D-050 เท่านั้น หน้าจอจึงพูดตามจริงว่าเปลี่ยนจากที่นี่
   * ไม่ได้ แทนที่จะเรนเดอร์ฟอร์มที่ไม่มีอะไรไปยื่นให้เซิร์ฟเวอร์
   */
  const passwordChangeRequired =
    failure?.kind === 'result' && failure.result.status === 'password-change-required';

  const errorMessage =
    failure === null || passwordChangeRequired
      ? null
      : failure.kind === 'missing-input'
        ? MISSING_INPUT_MESSAGE
        : describeFailure(failure.result);

  return (
    <LoginCard deviceKind={deviceKind} screen="login">
      <>
        <h1 className="cap-login__title">เข้าสู่ระบบ</h1>
        <p className="cap-login__sub">
          ระบบยังไม่ทราบว่าคุณเป็นใคร จึงปิดเมนูและหน้าจอธุรกิจทั้งหมดไว้ก่อน (deny-safe)
        </p>

        {passwordChanged ? (
          <p className="cap-login__ended" role="status" data-testid="login-password-changed">
            {PASSWORD_CHANGED_MESSAGE}
          </p>
        ) : null}

        {endedReason === null ? null : (
          <p className="cap-login__ended" role="status" data-testid="session-ended">
            {endedReason === 'SIGNED_OUT'
              ? 'ออกจากระบบเรียบร้อยแล้ว'
              : 'เซสชันก่อนหน้านี้ใช้ไม่ได้แล้ว (หมดอายุหรือถูกยกเลิก) — กรุณาเข้าสู่ระบบอีกครั้ง'}
          </p>
        )}

        {/*
          `method="post"` ระบุไว้ตรง ๆ แม้จะ `preventDefault()` เสมอ: ฟอร์มที่ถูก
          submit แบบ GET จะพารหัสผ่านไปอยู่ใน query string ของ URL แล้วต่อไปอยู่ใน
          history และ access log ซึ่ง `docs/SECURITY.md` ห้ามไว้
        */}
        <form className="cap-login__form" method="post" onSubmit={submit} aria-label="เข้าสู่ระบบ">
          <div className="cap-login__field">
            <label className="cap-login__label" htmlFor="cap-login-username">
              ชื่อผู้ใช้
            </label>
            <input
              id="cap-login-username"
              ref={usernameRef}
              className="cap-login__input"
              style={fieldStyle}
              name="username"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              disabled={submitting}
            />
          </div>

          <div className="cap-login__field">
            <label className="cap-login__label" htmlFor="cap-login-password">
              รหัสผ่าน
            </label>
            <input
              id="cap-login-password"
              ref={passwordRef}
              className="cap-login__input"
              style={fieldStyle}
              name="password"
              type="password"
              autoComplete="current-password"
              disabled={submitting}
            />
          </div>

          {errorMessage === null ? null : (
            <p className="cap-login__error" role="alert" data-testid="login-error">
              {errorMessage}
            </p>
          )}

          {passwordChangeRequired ? (
            /*
             * `403` + `details.rule = PASSWORD_CHANGE_REQUIRED` แต่ **ไม่มี**
             * `password_change_token` มาด้วย — คำตอบของเซิร์ฟเวอร์รุ่นก่อน D-050
             *
             * เส้นทางปกติไม่ผ่านตรงนี้: พอ token มาด้วย หน้าจอจะสลับไปฟอร์มตั้ง
             * รหัสผ่านใหม่ก่อนถึงบรรทัดนี้ ที่นี่จึงเหลือไว้บอกตามจริงว่าเปลี่ยนจาก
             * หน้าจอนี้ไม่ได้ **โดยไม่ส่งผู้ใช้ไปหาผู้ดูแลระบบให้รีเซ็ตซ้ำ** ซึ่งจะ
             * พากลับมาที่สถานะเดิมนี้อีกใบหนึ่ง
             */
            <div
              className="cap-login__notice"
              role="alert"
              data-testid="login-password-change-required"
            >
              <strong>ต้องเปลี่ยนรหัสผ่านก่อนใช้งาน</strong>
              <span>
                รหัสผ่านของบัญชีนี้ถูกตั้งให้ต้องเปลี่ยนก่อนเข้าใช้งาน เซิร์ฟเวอร์จึงยังไม่ออกสิทธิ์การใช้งานให้
              </span>
              <span>
                แต่คำตอบครั้งนี้ไม่ได้แนบสิทธิ์สำหรับตั้งรหัสผ่านใหม่มาด้วย จึงยังตั้งรหัสผ่านใหม่จากหน้าจอนี้ไม่ได้ —
                ลองเข้าสู่ระบบอีกครั้ง
              </span>
            </div>
          ) : null}

          <button
            type="submit"
            className="cap-login__submit"
            style={handheld ? primaryTouchTargetStyle : undefined}
            disabled={submitting}
            data-testid="login-submit"
          >
            {submitting ? 'กำลังเข้าสู่ระบบ…' : 'เข้าสู่ระบบ'}
          </button>
        </form>

        <p className="cap-login__note" data-testid="login-device-kind" data-value={deviceKind}>
          {describeDeviceKind(deviceKind)}
        </p>
        <p className="cap-login__note">
          การซ่อน/ปิดหน้าจอที่ฝั่งเบราว์เซอร์เป็นเพียง UX — ผู้บังคับสิทธิ์จริงคือ backend ทุกคำขอเสมอ
        </p>
      </>
    </LoginCard>
  );
}
